import React from 'react';

import {profileDetailsGenericDataItemT} from '@/lib/types/form.types';

import {usePagination} from '@/hooks';

/**
 * This component is used to render data with no action items and no images.
 * @param {string} title - The title of the data.
 * @param {object[]} data - The data to be rendered.
 * @param {string} className - The class name of the component.
 * @param {number} [itemsPerPage] - The number of items to be displayed per page.
 * @returns {React.ReactNode} - The generic data component.
 */
export const ProfileDetailsDataItemGenericComponent: React.FC<profileDetailsGenericDataItemT> = ({
  title,
  data,
  className,
  itemsPerPage = 5,
  stats,
  action,
  dataActions,
  filters,
}: profileDetailsGenericDataItemT) => {
  const {currentPage, totalPages, currentData, handlePrev, handleNext, goToPage} = usePagination(data, itemsPerPage);

  const getColumnSpan = () => {
    switch (stats?.length) {
      case 2:
        return 'col-span-6';
      case 3:
        return 'col-span-4';
      case 4:
      default:
        return 'col-span-3';
    }
  };

  return (
    <div className={`box ${className} flex h-min flex-col`}>
      <div className="box-header">
        <div className="box-title">{title}</div>
        {filters && filters.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {filters.map((filter, index) => (
              <select
                key={index}
                className="ti-form-select rounded-md border-gray-300 text-sm"
                onChange={e => filter.handler(e.target.value)}
              >
                <option value="">{filter.title}</option>
                {filter.options.map((option, optionIndex) => (
                  <option key={optionIndex} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ))}
          </div>
        )}
        {action && (
          <button
            className="ti-btn ti-btn-primary !m-0 h-full !gap-0 !bg-primary !px-2 !py-1 !text-[0.75rem] !font-medium !text-white"
            onClick={action.handler}
          >
            {action.icon && <i className={action.icon}></i>}
            {action.text}
          </button>
        )}
      </div>
      {/* Stats Section */}
      {stats && stats.length > 0 && (
        <div className="grid grid-cols-12 gap-2 p-2">
          {stats.map((stat, index) => (
            <div key={index} className={getColumnSpan()}>
              <div className={`flex items-center p-1 bg-${stat.color}/10 rounded-sm`}>
                <div className={`avatar bg-${stat.color} me-2 rounded-full p-1 text-white`}>
                  <i className={`${stat.icon} text-xs`}></i>
                </div>
                <div className="flex-grow">
                  <div className="text-xs font-semibold">{stat.title}</div>
                  <div className="text-sm font-bold">{stat.value}</div>
                  {stat.subtitle && <div className="text-xs text-gray-500">{stat.subtitle}</div>}
                </div>
                <div>
                  <span className={`text-xs font-semibold text-${stat.change > 0 ? 'green-500' : 'red-500'}`}>
                    {stat.change > 0 ? `+${stat.change}%` : `${stat.change}%`}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="box-body max-h-[300px] flex-grow-0 overflow-y-auto">
        <table className="w-full">
          <tbody>
            {currentData.map((item, index) => (
              <tr key={index} className="border-b last:border-b-0">
                {item.texts.map((textPair, textIndex) => (
                  <td key={textIndex} className="px-3 py-2">
                    <div className="font-semibold">{textPair.text1}</div>
                    <div className="text-[0.75rem] text-[#8c9097] dark:text-white/50">{textPair.text2}</div>
                  </td>
                ))}
                {dataActions && dataActions.length > 0 && (
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap justify-end gap-2">
                      {dataActions.map((action, actionIndex) => (
                        <button
                          key={actionIndex}
                          className="ti-btn-primary flex items-center justify-center whitespace-nowrap p-2"
                          onClick={() => action.handler(item.item)}
                        >
                          {action.icon && <i className={`${action.icon} mr-1`}></i>}
                          {action.text}
                        </button>
                      ))}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="box-footer">
        <div className="items-center sm:flex">
          <div className="m-auto">
            <nav aria-label="Page navigation" className="pagination-style-4">
              <ul className="ti-pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={handlePrev}>
                    Prev
                  </button>
                </li>
                {[...Array(totalPages).keys()].map(page => (
                  <li className={`page-item ${currentPage === page + 1 ? 'active' : ''}`} key={page}>
                    <button
                      className={`page-link ${currentPage === page + 1 ? 'active' : ''}`}
                      onClick={() => goToPage(page + 1)}
                    >
                      {page + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={handleNext}>
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};
